import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/stores/auth.store";
import { useTaskStatuses } from "@/hooks/useTaskStatuses";
import { useOpenTask } from "@/hooks/useOpenTask";
import { getCalendarEvents, type CalendarEvent } from "@/api/calendar";
import { listMembers } from "@/api/projects";
import { cn } from "@/lib/utils";
import { TaskDetailSheet } from "@/components/tasks/TaskDetailSheet";
import { TaskCreateDialog } from "@/components/tasks/TaskCreateDialog";
import { MonthView } from "@/components/calendar/MonthView";
import { WeekView } from "@/components/calendar/WeekView";
import type { ProjectMember } from "@/api/types";

type ViewMode = "month" | "week";
type Scope = "all" | "my_tasks";

export default function CalendarPage() {
  const navigate = useNavigate();
  const { project, roleName, isSuperadmin } = useCurrentProject();
  const perms = usePermissions();
  const currentUser = useAuthStore((s) => s.user);
  const slug = project?.slug ?? null;
  const taskStatuses = useTaskStatuses(slug);

  // Route guard — same gate as the Tasks page since the calendar
  // surfaces the same data source.
  useEffect(() => {
    if (slug && !perms.canViewTasks) {
      toast.info("You don't have access to this page.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [slug, perms.canViewTasks, navigate]);

  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [view, setView] = useState<ViewMode>(() => {
    const stored = localStorage.getItem("prism.calendar.view");
    return stored === "week" ? "week" : "month";
  });
  function setViewPersistent(next: ViewMode) {
    setView(next);
    localStorage.setItem("prism.calendar.view", next);
  }

  // Default scope mirrors the Tasks page rule — admins see "All",
  // everyone else lands on "My tasks".
  const [scope, setScope] = useState<Scope>(
    isSuperadmin || roleName === "Admin" ? "all" : "my_tasks",
  );
  const userTouchedScope = useRef(false);
  useEffect(() => {
    if (userTouchedScope.current) return;
    setScope(isSuperadmin || roleName === "Admin" ? "all" : "my_tasks");
  }, [isSuperadmin, roleName]);
  function setScopeManual(next: Scope) {
    userTouchedScope.current = true;
    setScope(next);
  }

  // The visible range depends on the view. For month view we expand
  // out to the first Monday before / last Sunday after so border days
  // (greyed) still resolve their events.
  // (Named `viewRange` rather than `window` to avoid shadowing the
  // global — Vite renames a local `window` to `window2` and the
  // useState initialiser above blows up in TDZ.)
  const viewRange = useMemo(() => {
    if (view === "month") {
      const start = startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 });
      return { start, end };
    }
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    const end = endOfWeek(anchor, { weekStartsOn: 1 });
    return { start, end };
  }, [view, anchor]);

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      const data = await getCalendarEvents(slug, {
        start: viewRange.start.toISOString(),
        end: viewRange.end.toISOString(),
        assignedToMe: scope === "my_tasks",
      });
      setEvents(data);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Couldn't load the calendar.",
      );
    } finally {
      setLoading(false);
    }
  }, [slug, viewRange.start, viewRange.end, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  const [members, setMembers] = useState<ProjectMember[]>([]);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    listMembers(slug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ─── Detail / create dialogs ──────────────────────────────────────
  const calendarTasks = useMemo(
    () => events.flatMap((e) => (e.type === "task" ? [e.task] : [])),
    [events],
  );
  const [detailTask, setDetailTask] = useOpenTask("calendar", calendarTasks);
  const [createDate, setCreateDate] = useState<Date | null>(null);

  function handleOpenEvent(event: CalendarEvent) {
    if (event.type === "task") setDetailTask(event.task);
  }

  function handleCreateAt(date: Date) {
    // Calendar event creation is gated on the dedicated
    // calendar.create_event permission; the legacy tasks.create
    // check is folded in via canCreateCalendarEvent's fallback.
    if (!perms.canCreateCalendarEvent) return;
    setCreateDate(date);
  }

  // Default the create dialog to noon on the picked day so the user
  // sees a sensible time pre-filled. They can edit before saving.
  const defaultDueAt = useMemo(() => {
    if (!createDate) return null;
    const d = new Date(createDate);
    d.setHours(12, 0, 0, 0);
    return d.toISOString();
  }, [createDate]);

  // ─── Navigation ───────────────────────────────────────────────────
  function navPrev() {
    setAnchor((a) => (view === "month" ? addMonths(a, -1) : addWeeks(a, -1)));
  }
  function navNext() {
    setAnchor((a) => (view === "month" ? addMonths(a, 1) : addWeeks(a, 1)));
  }
  function navToday() {
    setAnchor(new Date());
  }

  const headerLabel = useMemo(() => {
    if (view === "month") return format(anchor, "MMMM yyyy");
    const start = startOfWeek(anchor, { weekStartsOn: 1 });
    const end = endOfWeek(anchor, { weekStartsOn: 1 });
    if (start.getMonth() === end.getMonth()) {
      return `${format(start, "MMM d")} – ${format(end, "d, yyyy")}`;
    }
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }, [view, anchor]);

  if (!perms.canViewTasks || !project) return null;

  return (
    <div className="space-y-6 pb-12">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Calendar
          </h1>
          <p className="text-sm text-slate-500">{project.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <div
            role="group"
            aria-label="View"
            className="inline-flex h-9 rounded-lg border border-slate-200 bg-slate-50 p-0.5"
          >
            <ViewToggleButton
              active={view === "month"}
              label="Month"
              onClick={() => setViewPersistent("month")}
            />
            <ViewToggleButton
              active={view === "week"}
              label="Week"
              onClick={() => setViewPersistent("week")}
            />
          </div>
        </div>
      </header>

      {/* Filter row + navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={navPrev}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <h2 className="min-w-[12rem] text-center text-base font-semibold text-slate-800">
            {headerLabel}
          </h2>
          <Button variant="outline" size="sm" onClick={navNext}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={navToday}>
            Today
          </Button>
        </div>

        <div
          role="group"
          aria-label="Scope"
          className="inline-flex h-9 rounded-lg border border-slate-200 bg-slate-50 p-0.5"
        >
          <ScopeButton
            active={scope === "all"}
            label="All"
            onClick={() => setScopeManual("all")}
          />
          <ScopeButton
            active={scope === "my_tasks"}
            label="My tasks"
            onClick={() => setScopeManual("my_tasks")}
          />
        </div>
      </div>

      {errorMessage ? (
        <div className="flex items-center gap-3 rounded-plane border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          <CalendarIcon className="h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="ml-auto border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
          >
            Retry
          </Button>
        </div>
      ) : view === "month" ? (
        <MonthView
          anchor={anchor}
          events={events}
          loading={loading}
          onCreateAt={handleCreateAt}
          onOpenEvent={handleOpenEvent}
          isDone={taskStatuses.isDone}
        />
      ) : (
        <WeekView
          anchor={anchor}
          events={events}
          loading={loading}
          onCreateAt={handleCreateAt}
          onOpenEvent={handleOpenEvent}
          isDone={taskStatuses.isDone}
        />
      )}

      {slug && (
        <TaskDetailSheet
          task={detailTask}
          open={!!detailTask}
          onOpenChange={(o) => {
            if (!o) setDetailTask(null);
          }}
          projectSlug={slug}
          onTaskUpdated={(updated) => {
            setEvents((prev) =>
              prev.map((e) =>
                e.type === "task" && e.id === updated.id
                  ? { ...e, task: updated, at: updated.due_at ?? e.at }
                  : e,
              ),
            );
            setDetailTask(updated);
          }}
          onTaskDeleted={(id) => {
            setEvents((prev) => prev.filter((e) => e.id !== id));
            setDetailTask(null);
          }}
        />
      )}

      {slug && createDate !== null && (
        <TaskCreateDialog
          projectSlug={slug}
          open={createDate !== null}
          onOpenChange={(o) => {
            if (!o) setCreateDate(null);
          }}
          members={members}
          defaultAssigneeId={currentUser?.id ?? ""}
          defaultStatus={taskStatuses.initialKey ?? null}
          taskStatuses={taskStatuses.statuses}
          defaultDueAt={defaultDueAt ?? undefined}
          onSaved={() => {
            void load();
          }}
        />
      )}
    </div>
  );
}

function ViewToggleButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium transition-colors",
        active
          ? "bg-white text-slate-900 shadow-sm-tile"
          : "text-slate-500 hover:text-slate-700",
      )}
    >
      {label}
    </button>
  );
}

function ScopeButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-8 items-center rounded-md px-3 text-xs font-medium transition-colors",
        active
          ? "bg-indigo-600 text-white shadow-sm-tile"
          : "text-slate-500 hover:text-slate-700",
      )}
    >
      {label}
    </button>
  );
}
